import { fetcher } from "@certn/api";
import { showToast } from "@certn/cascadia";
import {
  ActionList,
  ActionListItem,
  Checkbox,
  FormFeedbackRAC,
  LinkifiedText,
  List,
  Loader,
  Text,
  Upload,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { MutableRefObject, useRef, useState } from "react";
import type { FileDropItem } from "react-aria";
import { useTranslation } from "react-i18next";

import { CannotProvideReasonField } from "./CannotProvideReasonField";
import {
  documentRemovePath,
  DOCUMENT_CONTENT_TYPES,
  MAX_DOCUMENT_SIZE_BYTES,
} from "./constants";
import { useSaveCannotProvide } from "./hooks";
import { useInformationRequestSaves } from "./InformationRequestSaves";
import {
  InformationRequestDocument,
  InformationRequestItem,
  InformationRequestItemAnswer,
} from "./types";
import { DocumentRow } from "./DocumentRow";
import { getUploadProblem, uploadItemDocument } from "./uploads";

type Props = {
  caseId: string;
  item: InformationRequestItem;
  isReadOnly?: boolean;
  onAnswered: (answer: InformationRequestItemAnswer) => void;
  onDocumentsChanged: () => Promise<unknown>;
  onDraftChange?: (hasDraft: boolean) => void;
  saveDraftRef?: MutableRefObject<(() => Promise<boolean>) | null>;
};

type UploadingFile = { id: number; name: string };

export function DocumentResponseField({
  caseId,
  item,
  isReadOnly = false,
  onAnswered,
  onDocumentsChanged,
  onDraftChange,
  saveDraftRef,
}: Props) {
  const { t } = useTranslation();
  const { trackSave, waitForSaves } = useInformationRequestSaves();
  const saveCannotProvide = useSaveCannotProvide(caseId, item.id);
  const [cannotProvide, setCannotProvide] = useState(item.cannot_provide);
  const [isUndoing, setIsUndoing] = useState(false);
  const [uploading, setUploading] = useState<UploadingFile[]>([]);
  const [removingIds, setRemovingIds] = useState<string[]>([]);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const nextUploadId = useRef(0);
  const hasDocuments = item.documents.length > 0 || uploading.length > 0;

  const uploadFile = async (file: File) => {
    const problem = getUploadProblem(file);
    if (problem) {
      setUploadErrors((errors) => [
        ...errors,
        problem === "too-big"
          ? t("{{filename}} is larger than 10 MB.", { filename: file.name })
          : t("{{filename}} isn't a PDF, JPEG or PNG file.", {
              filename: file.name,
            }),
      ]);
      return;
    }
    const id = nextUploadId.current++;
    setUploading((files) => [...files, { id, name: file.name }]);
    try {
      await trackSave(uploadItemDocument(caseId, item.id, file));
      await onDocumentsChanged();
    } catch {
      setUploadErrors((errors) => [
        ...errors,
        t("{{filename}} couldn't be uploaded. Please try again.", {
          filename: file.name,
        }),
      ]);
    } finally {
      setUploading((files) => files.filter((upload) => upload.id !== id));
    }
  };

  const handleFilesSelected = async (selected: (File | FileDropItem)[]) => {
    setUploadErrors([]);
    const files = await Promise.all(
      selected.map((file) => ("getFile" in file ? file.getFile() : file))
    );
    await Promise.all(files.map(uploadFile));
  };

  const handleRemove = async (document: InformationRequestDocument) => {
    setRemovingIds((ids) => [...ids, document.id]);
    try {
      await trackSave(
        fetcher.submit(documentRemovePath(caseId, document.id), {})
      );
      await onDocumentsChanged();
    } catch {
      showToast(t("We couldn't remove this document. Please try again."), {
        type: "error",
      });
    } finally {
      setRemovingIds((ids) => ids.filter((id) => id !== document.id));
    }
  };

  const handleCannotProvideChange = (isSelected: boolean) => {
    if (isSelected) {
      setCannotProvide(true);
      return;
    }
    setIsUndoing(true);
    trackSave(
      waitForSaves().then(() => saveCannotProvide({ cannot_provide: false }))
    )
      .then((answer) => {
        setCannotProvide(false);
        if (answer) {
          onAnswered(answer);
        }
      })
      .catch(() => undefined)
      .finally(() => setIsUndoing(false));
  };

  if (isReadOnly) {
    return (
      <div className={styles.field}>
        {item.documents.length > 0 && (
          <div
            className={styles.documents}
            data-testid={`information-request-documents-${item.id}`}
          >
            <Text style="input/label">{t("Uploaded")}</Text>
            {item.documents.map((document) => (
              <DocumentRow
                key={document.id}
                caseId={caseId}
                document={document}
                isRemoving={false}
              />
            ))}
          </div>
        )}
        {item.cannot_provide && (
          <div
            className={styles.documents}
            data-testid={`information-request-cannot-provide-${item.id}`}
          >
            <Text style="input/label">{t("You don't have this document")}</Text>
            {item.cannot_provide_reason && (
              <>
                <Text style="input/label">{t("Reason")}</Text>
                <Text
                  style="global/body-primary"
                  className={styles.reason}
                  testID={`information-request-cannot-provide-reason-${item.id}`}
                >
                  <LinkifiedText
                    text={item.cannot_provide_reason}
                    testID={`information-request-cannot-provide-reason-${item.id}-link`}
                  />
                </Text>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.field}>
      {!cannotProvide && (
        <>
          <Upload
            testID={`information-request-upload-${item.id}`}
            multiple
            fileTypes={DOCUMENT_CONTENT_TYPES}
            maxSizeBytes={MAX_DOCUMENT_SIZE_BYTES}
            onFileSelected={handleFilesSelected}
          />
          {uploadErrors.length === 1 && (
            <FormFeedbackRAC type="invalid">{uploadErrors[0]}</FormFeedbackRAC>
          )}
          {uploadErrors.length > 1 && (
            <FormFeedbackRAC type="invalid">
              {t("The following files failed to upload:")}
              <List type="unordered">
                {uploadErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </List>
            </FormFeedbackRAC>
          )}
          {hasDocuments && (
            <div
              className={styles.documents}
              data-testid={`information-request-documents-${item.id}`}
            >
              {item.documents.map((document) => (
                <DocumentRow
                  key={document.id}
                  caseId={caseId}
                  document={document}
                  isRemoving={removingIds.includes(document.id)}
                  onRemove={handleRemove}
                />
              ))}
              {uploading.map((file) => (
                <ActionList key={file.id}>
                  <ActionListItem
                    testID="information-request-uploading-document"
                    leading={<Loader type="spinner" size="small" />}
                    title={file.name}
                  />
                </ActionList>
              ))}
            </div>
          )}
        </>
      )}
      {!hasDocuments && (
        <Checkbox
          testID={`information-request-cannot-provide-${item.id}`}
          isSelected={cannotProvide}
          onChange={handleCannotProvideChange}
          isDisabled={isUndoing}
        >
          {t("I don't have this document")}
        </Checkbox>
      )}
      {cannotProvide && (
        <CannotProvideReasonField
          caseId={caseId}
          item={item}
          onAnswered={onAnswered}
          isDisabled={isUndoing}
          onDraftChange={onDraftChange}
          saveDraftRef={saveDraftRef}
        />
      )}
    </div>
  );
}

const styles = {
  reason: css({
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  }),
  field: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  documents: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
};
